import * as React from 'react';
import isNil from 'lodash/isNil';
import { connect } from 'react-redux';

import Widget from './widget/Widget';

import {
  deleteSnippets,
  flushDeliverable,
  initForDeliverable,
  updateCanEditForDeliverable,
  validateDeliverable
} from '../../redux/actions';

import {
  State
} from 'types';
import {
  StorySnippetsPayload
} from '../../types/general';

export interface OwnProps extends StorySnippetsPayload {
  externallyDeletedSnippetAttachments: Array<number>,
  orgType: 'brand' | 'influencer'
}

interface StateProps {
  status: 'initialized' | 'not-initialized'
}

interface DispatchProps {
  dispatchInitialize: (d: StorySnippetsPayload) => void,
  dispatchUpdateCanEditForDeliverable: (canEdit: boolean, deliverableId: number) => void,
  dispatchFlush: (deliverableId: number) => void,
  dispatchValidateDeliverable: (deliverableId: number, timestamp: null | number) => void,
  dispatchDeleteSnippets: (deliverableId: number, snippetIds: Array<number>) => void
}

type Props = OwnProps & StateProps & DispatchProps;

const StorySnippetsLinkedWithAnalytics = ({
  orgType,
  canEdit,
  campaignId,
  deliverable,
  externallyDeletedSnippetAttachments,
  lastValidateAt,
  status,
  dispatchDeleteSnippets,
  dispatchInitialize,
  dispatchUpdateCanEditForDeliverable,
  dispatchFlush,
  dispatchValidateDeliverable,
  onAttachmentsUpdate,
  onAttachmentsDelete,
  onAttachmentReplace,
  onStorySnippetEdit
}: Props) => {
  React.useEffect(() => {
    dispatchInitialize({
      canEdit,
      campaignId,
      deliverable,
      lastValidateAt,
      onAttachmentsUpdate,
      onAttachmentsDelete,
      onAttachmentReplace,
      onStorySnippetEdit
    });

    return () => {
      dispatchFlush(deliverable.id);
    };
  }, [deliverable.id]);

  React.useEffect(() => {
    dispatchUpdateCanEditForDeliverable(canEdit, deliverable.id);
  }, [canEdit]);

  React.useEffect(() => {
    dispatchValidateDeliverable(deliverable.id, lastValidateAt);
  }, [lastValidateAt]);

  React.useEffect(() => {
    dispatchDeleteSnippets(deliverable.id, externallyDeletedSnippetAttachments);
  }, [externallyDeletedSnippetAttachments]);

  if (status === 'not-initialized') {
    return null;
  }

  return (
    <Widget
      orgType={orgType}
      deliverableId={deliverable.id}
    />
  );
};

const mapStateToProps = ({ storySnippets }: State, { deliverable }: OwnProps): StateProps => ({
  status: isNil(storySnippets.deliverables[deliverable.id])
    ? 'not-initialized'
    : 'initialized'
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchInitialize: (details) => dispatch(initForDeliverable(details)),
  dispatchUpdateCanEditForDeliverable: (canEdit, deliverableId) => dispatch(
    updateCanEditForDeliverable(deliverableId, canEdit)
  ),
  dispatchFlush: deliverableId => dispatch(flushDeliverable(deliverableId)),
  dispatchValidateDeliverable: (id, timestamp) => dispatch(validateDeliverable(id, timestamp)),
  dispatchDeleteSnippets: (id, ids) => dispatch(deleteSnippets(id, ids))
});

export default connect(mapStateToProps, mapDispatchToProps)(StorySnippetsLinkedWithAnalytics);
